import { Match, Show, Switch, createSignal, onCleanup, onMount } from "solid-js";
import type { AuthModule, AuthSession, AuthUser } from "~/features/auth/auth";
import type { HttpPort } from "~/lib/http";
import { StudentHelpModal } from "./StudentHelpModal";
import { StudentCheckinPage } from "./StudentCheckinPage";
import { StudentHomePage } from "./StudentHomePage";
import { StudentJoinPage } from "./StudentJoinPage";
import { StudentMoodUpdatePage } from "./StudentMoodUpdatePage";
import { StudentMonsterPage } from "./StudentMonsterPage";
import { StudentProgressPage } from "./StudentProgressPage";
import { StudentVotingPage } from "./StudentVotingPage";
import { StudentProposalPage } from "./StudentProposalPage";
import { StudentFeedbackPage } from "./StudentFeedbackPage";
import { StudentMyClassPage } from "./StudentMyClassPage";
import { StudentTaskSelectionPage } from "./StudentTaskSelectionPage";
import { StudentShell, type StudentView } from "./StudentShell";
import { createStudentModule, type StudentClass } from "./student";
import { createStudentHomeModule } from "./studentHome";
import { createStudentCheckinModule } from "./studentCheckin";
import { createStudentJourneyModule } from "./studentJourney";
import { createStudentVotingModule } from "./studentVoting";
import { createStudentProposalsModule } from "./studentProposals";
import { createStudentFeedbackModule } from "./studentFeedback";
import { createStudentMyClassModule } from "./studentMyClass";

type Toast = { type: "success" | "error"; text: string };

export function StudentDashboard(props: { session: AuthSession; auth: AuthModule; http: HttpPort; onLogout: () => void }) {
  const studentModule = createStudentModule(props.http, props.session.token);
  const homeModule = createStudentHomeModule(props.http, props.session.token);
  const checkinModule = createStudentCheckinModule(props.http, props.session.token);
  const journeyModule = createStudentJourneyModule(props.http, props.session.token);
  const votingModule = createStudentVotingModule(props.http, props.session.token);
  const proposalsModule = createStudentProposalsModule(props.http, props.session.token);
  const feedbackModule = createStudentFeedbackModule(props.http, props.session.token);
  const myClassModule = createStudentMyClassModule(props.http, props.session.token);
  const [user, setUser] = createSignal<AuthUser>(props.session.user);
  const [classes, setClasses] = createSignal<StudentClass[]>([]);
  const [selectedClass, setSelectedClass] = createSignal<StudentClass>();
  const [view, setView] = createSignal<StudentView>("home");
  const [sidebarOpen, setSidebarOpen] = createSignal(false);
  const [helpOpen, setHelpOpen] = createSignal(false);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [toast, setToast] = createSignal<Toast>();
  let toastTimer: number | undefined;

  const notify = (type: Toast["type"], text: string) => {
    window.clearTimeout(toastTimer);
    setToast({ type, text });
    toastTimer = window.setTimeout(() => setToast(undefined), 3000);
  };
  const rememberClass = (value: StudentClass | undefined) => {
    setSelectedClass(value);
    props.auth.selectClass(value ? { id: value.id, name: value.name } : undefined);
  };
  const load = async (preferredClassId?: string) => {
    const workspace = await studentModule.load(preferredClassId);
    setClasses(workspace.classes);
    const updatedUser = props.auth.updateUser(workspace.user) ?? workspace.user;
    setUser(updatedUser);
    rememberClass(workspace.selectedClass);
    setView(workspace.selectedClass ? "home" : "join");
  };
  const join = async (joinCode: string) => {
    try {
      const joined = await studentModule.join(joinCode);
      await load(joined.id);
      notify("success", `${joined.name} beigetreten! 🎉`);
      return joined;
    } catch (cause) {
      notify("error", cause instanceof Error ? cause.message : "Klasse konnte nicht beigetreten werden");
      throw cause;
    }
  };
  const logout = async () => {
    await props.auth.logout().catch(() => {});
    props.onLogout();
  };

  onMount(() => {
    document.body.className = "role-student";
    void load(props.session.currentClass?.id)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Klassen konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  });
  onCleanup(() => {
    window.clearTimeout(toastTimer);
    document.body.className = "";
  });

  return <StudentShell
    user={user()}
    selectedClass={selectedClass()}
    view={view()}
    sidebarOpen={sidebarOpen()}
    onSidebarOpenChange={setSidebarOpen}
    onNavigate={setView}
    onHelp={() => setHelpOpen(true)}
    onLogout={logout}
  >
    <Show when={toast()}>{(message) => <div class="toast-container"><div class={`toast ${message().type}`} role={message().type === "error" ? "alert" : "status"}>{message().text}</div></div>}</Show>
    <Show when={!loading()} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "60px" }}><div class="loading-spinner" /></div>}>
      <Show when={!error()} fallback={<div class="card"><p>{error()}</p><button class="btn btn-primary" type="button" style={{ "margin-top": "12px" }} onClick={() => window.location.reload()}>Erneut versuchen</button></div>}>
        <Switch fallback={<div class="card"><p>Diese Ansicht konnte nicht geladen werden.</p></div>}>
          <Match when={view() === "home"}><StudentHomePage value={selectedClass()} user={user()} module={homeModule} onNavigate={setView} /></Match>
          <Match when={view() === "checkin"}><StudentCheckinPage value={selectedClass()} module={checkinModule} onHome={() => setView("home")} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "task-selection"}><StudentTaskSelectionPage value={selectedClass()} module={checkinModule} onHome={() => setView("home")} onSuccess={(message) => notify("success", message)} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "mood-update"}><StudentMoodUpdatePage value={selectedClass()} module={checkinModule} onHome={() => setView("home")} onSuccess={(message) => notify("success", message)} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "monster"}><StudentMonsterPage value={selectedClass()} user={user()} module={journeyModule} onSuccess={(message) => notify("success", message)} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "progress"}><StudentProgressPage value={selectedClass()} module={journeyModule} onCheckin={() => setView("checkin")} /></Match>
          <Match when={view() === "voting"}><StudentVotingPage value={selectedClass()} module={votingModule} onHome={() => setView("home")} onSuccess={(message) => notify("success", message)} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "propose"}><StudentProposalPage value={selectedClass()} module={proposalsModule} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "feedback"}><StudentFeedbackPage value={selectedClass()} module={feedbackModule} onHelp={() => setHelpOpen(true)} onSuccess={(message) => notify("success", message)} onError={(message) => notify("error", message)} /></Match>
          <Match when={view() === "my-class"}><StudentMyClassPage value={selectedClass()} module={myClassModule} /></Match>
          <Match when={view() === "join"}><StudentJoinPage canCancel={classes().length > 0} onCancel={() => setView("home")} onJoin={join} /></Match>
        </Switch>
      </Show>
    </Show>
    <Show when={helpOpen()}><StudentHelpModal classId={selectedClass()?.id} module={studentModule} onClose={() => setHelpOpen(false)} onSuccess={() => notify("success", "✅ Die Lehrkräfte deiner Klasse wurden informiert. Du bist nicht allein!")} onError={(message) => notify("error", message)} /></Show>
  </StudentShell>;
}
